import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { throttle } from "lodash";
import DynamicPinturaEditor from "../../../components/DynamicPinturaEditor";
import { useAppContext } from "../../../context/appContext";
import authApi from "../../../service/IdApi";

/**
 * Crop a JobStock conversion-source image via Pintura, then upload the result
 * as a brand-new JobStock item (full variant + vision pipeline).
 */
const JobStockCropDialog = ({
    imageUrl,
    visible,
    onHide,
    onCreated,
}) => {
    const { displayAlert } = useAppContext();
    const [isProcessing, setIsProcessing] = useState(false);
    const [editorConfig, setEditorConfig] = useState(null);
    const [revisedImageUrl, setRevisedImageUrl] = useState(null);
    const [revisedContentType, setRevisedContentType] = useState(null);
    const [revisedFileName, setRevisedFileName] = useState(null);
    const [createdUrls, setCreatedUrls] = useState([]);

    const createAndTrackUrl = (blob) => {
        const url = URL.createObjectURL(blob);
        setCreatedUrls((prev) => [...prev, url]);
        return url;
    };

    const resetEditor = () => {
        setRevisedImageUrl(null);
        setRevisedContentType(null);
        setRevisedFileName(null);
        if (imageUrl) {
            setEditorConfig({
                src: imageUrl,
            });
        } else {
            setEditorConfig(null);
        }
    };

    useEffect(() => {
        if (!visible) {
            return;
        }
        resetEditor();
    }, [visible, imageUrl]);

    useEffect(() => {
        return () => {
            createdUrls.forEach((url) => {
                URL.revokeObjectURL(url);
            });
        };
    }, [createdUrls]);

    const handleEditorResult = (result) => {
        if (!result) {
            return;
        }
        const imageUrlFromResult = createAndTrackUrl(result.dest);
        setEditorConfig({
            src: imageUrlFromResult,
        });
        setRevisedImageUrl(imageUrlFromResult);
        setRevisedContentType(result.dest.type);
        setRevisedFileName(result.dest.name);
    };

    const submitAction = throttle(async () => {
        if (!revisedImageUrl) {
            return;
        }
        setIsProcessing(true);
        try {
            const response = await fetch(revisedImageUrl);
            const blob = await response.blob();
            const fileName = revisedFileName || "cropped-image.jpg";
            const contentType = revisedContentType || blob.type || "image/jpeg";
            const file = new File([blob], fileName, { type: contentType });

            const formData = new FormData();
            formData.append("file", file);
            await authApi.post("admin/jobstock/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            displayAlert("success", "Created", "Cropped JobStock item created successfully");
            if (onCreated) {
                onCreated();
            }
            onHide();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Create Failed", message);
        } finally {
            setIsProcessing(false);
        }
    }, 500, { trailing: false });

    const handleHide = () => {
        if (isProcessing) {
            return;
        }
        onHide();
    };

    return (
        <Dialog
            visible={visible}
            style={{ width: "95vw", height: "95vh" }}
            header="Crop JobStock Image"
            modal
            onHide={handleHide}
            footer={(
                <div>
                    {revisedImageUrl && (
                        <>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text p-button-danger"
                                onClick={handleHide}
                                disabled={isProcessing}
                            />
                            <Button
                                label="Create Cropped Image"
                                icon="pi pi-check"
                                onClick={submitAction}
                                loading={isProcessing}
                                disabled={isProcessing}
                            />
                        </>
                    )}
                </div>
            )}
            className="p-fluid"
        >
            <div className="mt-2 mb-2" style={{ height: "90%", width: "100%" }}>
                {revisedImageUrl && (
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                maxWidth: "100%",
                                overflow: "hidden",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={revisedImageUrl}
                                alt="Cropped image preview"
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    </div>
                )}
                {!revisedImageUrl && (
                    <div
                        style={{
                            height: "100%",
                            width: "100%",
                        }}
                    >
                        {editorConfig && (
                            <DynamicPinturaEditor
                                {...editorConfig}
                                onProcess={handleEditorResult}
                            />
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default JobStockCropDialog;
